import { CompositeLayer } from "@deck.gl/core";
import { IconLayerProps, ScatterplotLayer, ScatterplotLayerProps } from "@deck.gl/layers";
import { Feature, Position } from "geojson";

import { RotatingNodeLayer } from "./rotating-icon/CustomRotatingIconLayer";

export type NodePoint = {
  coordinates: Position;
  feature: Feature;
  name: string;
  showIcon: boolean;
};

/** Properties added by WaterFlow layer. */
type NodeIconLayerOwnProps<DataT = any> = {
  currentTime?: number;
  getRotate?: ((d: any) => 1 | 0) | 1 | 0;
  getIconSize: ((d: any) => number) | number;
  getIconColor: (() => number[]) | number[];
};

export type NodeIconLayerProps<DataT = any> = NodeIconLayerOwnProps<DataT> &
  ScatterplotLayerProps<DataT> &
  IconLayerProps<DataT>;

export class NodeIconLayer<DataT = any, ExtraProps extends object = object> extends CompositeLayer<
  Required<NodeIconLayerProps>
> {
  static override layerName = "NodeIconLayer";

  renderLayers() {
    return [
      // the nodes
      new ScatterplotLayer(
        this.getSubLayerProps({
          // `getSubLayerProps` will concat the parent layer id with this id
          id: this.props.id + "-background",
          data: this.props.data,
          getPosition: this.props.getPosition,

          getFillColor: this.props.getFillColor || [0, 0, 0],
          getRadius: this.props.getRadius,
          radiusUnits: this.props.radiusUnits || "meters",
          radiusScale: this.props.radiusScale || 1,
          radiusMinPixels: this.props.radiusMinPixels || 0,
          radiusMaxPixels: this.props.radiusMaxPixels || 999999,

          getLineWidth: this.props.getLineWidth || 0,
          getLineColor: this.props.getLineColor || [0, 0, 0],
          lineWidthUnits: this.props.lineWidthUnits || "meters",
          lineWidthScale: this.props.lineWidthScale || 1,
          lineWidthMinPixels: this.props.lineWidthMinPixels || 0,
          lineWidthMaxPixels: this.props.lineWidthMaxPixels || 999999,

          stroked: this.props.stroked ? true : false,
          filled: this.props.filled === undefined ? true : this.props.filled,
          antialiasing: this.props.antialiasing ? true : false,

          billboard: this.props.billboard ? true : false,

          updateTriggers: {
            getPosition: this.props.updateTriggers.getPosition,
            getFillColor: this.props.updateTriggers.getFillColor,
            getLineColor: this.props.updateTriggers.getLineColor,

            getRadius: this.props.updateTriggers.getRadius,

            getLineWidth: this.props.updateTriggers.getLineWidth,
          },
        })
      ),
      // the icons
      new RotatingNodeLayer(
        this.getSubLayerProps({
          // `getSubLayerProps` will concat the parent layer id with this id
          id: this.props.id + "-icon",
          data: this.props.data,
          getPosition: this.props.getPosition,

          currentTime: this.props.currentTime || 0,
          getRotate: this.props.getRotate || 0,
          getIcon: this.props.getIcon,
          iconAtlas: this.props.iconAtlas,
          iconMapping: this.props.iconMapping,
          loadOptions: this.props.loadOptions,
          onIconError: this.props.onIconError || null,

          getColor: this.props.getIconColor || [0, 0, 0],
          getSize: this.props.getIconSize,
          sizeScale: this.props.sizeScale || 1,
          sizeUnits: this.props.sizeUnits || "meters",
          sizeMinPixels: this.props.sizeMinPixels || 0,
          sizeMaxPixels: this.props.sizeMaxPixels || 999999,

          billboard: this.props.billboard ? true : false,
          alphaCutoff: this.props.alphaCutoff || 0.05,

          transitions: { currentTime: this.props.currentTime },
          updateTriggers: {
            getRotate: this.props.updateTriggers.getRotate,
            getPosition: this.props.updateTriggers.getPosition,
            getIcon: this.props.updateTriggers.getIcon,
            getSize: this.props.updateTriggers.getIconSize,
            getColor: this.props.updateTriggers.getIconColor,
          },
        })
      ),
    ];
  }
}
